import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { axiosGateway, axiosUsers } from "../../../../api/axios";
import ENDPOINTS from "../../../../api/endpoints";
import { fetchCardsByPatient } from "../../../redux/thunks/cnssThunks";

import "./NephrologyProfilePanel.css";

export default function NephrologyProfilePanel({ patient, onClose }) {
  const { t } = useTranslation("neurologue");
  const dispatch = useDispatch();
  const patientName = patient?.fullName || patient?.name || "—";
  const patientId = patient?.userId || patient?.id;
  const isLocalPatient = Boolean(patient?.isLocalPatient);
  const additionalInfoClient = isLocalPatient ? axiosGateway : axiosUsers;
  const additionalInfoPath = isLocalPatient
    ? `/local-patients${ENDPOINTS.LOCAL_PATIENT.ADDITIONAL_INFO(patientId)}`
    : ENDPOINTS.USERS.ADDITIONAL_INFO(patientId);
  const patientIdentifier = patient?.identifier || patient?.patientId || "";
  const gender = String(patient?.gender || "").trim().toLowerCase();
  const isFemale = ["female", "f", "woman", "femme", "féminin", "feminin", "أنثى"].includes(gender);
  const [form, setForm] = useState({
    maritalStatus: "",
    address: "",
    secondPhone: "",
    profession: "",
    treatingPhysician: "",
    referringPhysician: "",
    numberOfChildren: "",
    numberOfPregnancies: "",
    pregnancyComplications: "",
    curettageOrStoppedPregnanciesCount: "",
  });
  const [additionalInfoLoading, setAdditionalInfoLoading] = useState(false);
  const [additionalInfoError, setAdditionalInfoError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cnssCard, setCnssCard] = useState(null);
  const [cnssLoading, setCnssLoading] = useState(false);
  const [cnssError, setCnssError] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!patientId) return undefined;

    let active = true;
    setCnssCard(null);
    setAdditionalInfoError(false);
    setCnssError(false);
    setAdditionalInfoLoading(true);
    setCnssLoading(true);
    setForm({
      maritalStatus: "",
      address: "",
      secondPhone: "",
      profession: "",
      treatingPhysician: "",
      referringPhysician: "",
      numberOfChildren: "",
      numberOfPregnancies: "",
      pregnancyComplications: "",
      curettageOrStoppedPregnanciesCount: "",
    });

    additionalInfoClient
      .get(additionalInfoPath)
      .then(({ data }) => {
        if (!active) return;
        const info = data?.data ?? data ?? {};
        setForm({
          maritalStatus: info.maritalStatus ?? "",
          address: info.address ?? "",
          secondPhone: info.secondPhone ?? "",
          profession: info.profession ?? "",
          treatingPhysician: info.treatingPhysician ?? "",
          referringPhysician: info.referringPhysician ?? "",
          numberOfChildren: info.numberOfChildren ?? "",
          numberOfPregnancies: info.numberOfPregnancies ?? "",
          pregnancyComplications: info.pregnancyComplications ?? "",
          curettageOrStoppedPregnanciesCount: info.curettageOrStoppedPregnanciesCount ?? "",
        });
      })
      .catch(() => {
        if (active) setAdditionalInfoError(true);
      })
      .finally(() => {
        if (active) setAdditionalInfoLoading(false);
      });

    dispatch(fetchCardsByPatient(patientId))
      .unwrap()
      .then((payload) => {
        if (!active) return;
        const cards = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.cards ?? [];
        setCnssCard(Array.isArray(cards) ? cards[0] ?? null : null);
      })
      .catch(() => {
        if (active) setCnssError(true);
      })
      .finally(() => {
        if (active) setCnssLoading(false);
      });

    return () => {
      active = false;
    };
  }, [additionalInfoClient, additionalInfoPath, dispatch, patientId]);

  const fieldValue = (value) => (value === null || value === undefined || value === "" ? "—" : String(value));
  const cnssNumber = cnssCard?.identifiantUnique || "";
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSaveError(false);
    setSaveSuccess(false);
  };
  const saveAdditionalInfo = async () => {
    if (!patientId || isSaving) return;
    const integerFields = [
      form.numberOfChildren,
      ...(isFemale ? [form.numberOfPregnancies, form.curettageOrStoppedPregnanciesCount] : []),
    ];
    if (integerFields.some((value) => value !== "" && (!Number.isInteger(Number(value)) || Number(value) < 0))) {
      setSaveError("invalid");
      setSaveSuccess(false);
      return;
    }
    setIsSaving(true);
    setSaveError(false);
    setSaveSuccess(false);
    const payload = {
      maritalStatus: form.maritalStatus || null,
      address: form.address.trim() || null,
      secondPhone: form.secondPhone.trim() || null,
      profession: form.profession.trim() || null,
      treatingPhysician: form.treatingPhysician.trim() || null,
      referringPhysician: form.referringPhysician.trim() || null,
      numberOfChildren: form.numberOfChildren === "" ? null : Number(form.numberOfChildren),
      ...(isFemale
        ? {
          numberOfPregnancies: form.numberOfPregnancies === "" ? null : Number(form.numberOfPregnancies),
          pregnancyComplications: form.pregnancyComplications.trim() || null,
          curettageOrStoppedPregnanciesCount: form.curettageOrStoppedPregnanciesCount === ""
            ? null
            : Number(form.curettageOrStoppedPregnanciesCount),
        }
        : {}),
    };

    try {
      await additionalInfoClient.put(additionalInfoPath, payload);
      setSaveSuccess(true);
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="npp-overlay" onClick={onClose} aria-hidden="true" />

      <aside
        className="npp-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="npp-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="npp-header">
          <div className="npp-headerText">
            <h3 id="npp-title">{t("profilePanel.title")}</h3>
            <p>{t("profilePanel.patientMeta", { name: patientName, identifier: patientIdentifier || "—" })}</p>
          </div>

          <button
            type="button"
            className="npp-closeButton"
            aria-label={t("profilePanel.close")}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="npp-body">
          <section className="npp-section">
            <h4>{t("profilePanel.administrative")}</h4>

            <div className="npp-grid">
              <div className="npp-field">
                <label>{t("profilePanel.dossierNo")}</label>
                <input
                  value={fieldValue(patientIdentifier)}
                  readOnly
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.insuranceBooklet")}</label>
                <input
                  value={cnssNumber}
                  placeholder={cnssLoading ? t("profilePanel.loading") : t("profilePanel.noCnss")}
                  disabled={!cnssNumber}
                  readOnly
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.maritalStatus")}</label>
                <select
                  value={form.maritalStatus}
                  onChange={(event) => updateField("maritalStatus", event.target.value)}
                >
                  <option value="">{t("profilePanel.selectOption")}</option>
                  {["single", "married", "divorced", "other"].map((status) => (
                    <option value={status} key={status}>
                      {t(`profilePanel.maritalStatuses.${status}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.profession")}</label>
                <input
                  value={form.profession}
                  onChange={(event) => updateField("profession", event.target.value)}
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.address")}</label>
                <input
                  value={form.address}
                  onChange={(event) => updateField("address", event.target.value)}
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.secondPhone")}</label>
                <input
                  value={form.secondPhone}
                  onChange={(event) => updateField("secondPhone", event.target.value)}
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.treatingPhysician")}</label>
                <input
                  value={form.treatingPhysician}
                  onChange={(event) => updateField("treatingPhysician", event.target.value)}
                />
              </div>

              <div className="npp-field">
                <label>{t("profilePanel.referringPhysician")}</label>
                <input
                  value={form.referringPhysician}
                  onChange={(event) => updateField("referringPhysician", event.target.value)}
                />
              </div>
            </div>
            {additionalInfoLoading && <p className="npp-status">{t("profilePanel.loading")}</p>}
            {additionalInfoError && <p className="npp-status npp-status--error">{t("profilePanel.additionalInfoFailed")}</p>}
            {cnssError && <p className="npp-status npp-status--error">{t("profilePanel.cnssFailed")}</p>}
            {saveError && (
              <p className="npp-status npp-status--error" role="alert">
                {saveError === "invalid" ? t("profilePanel.saveInvalid") : t("profilePanel.saveFailed")}
              </p>
            )}
            {saveSuccess && <p className="npp-status npp-status--success" role="status">{t("profilePanel.saveSuccess")}</p>}
          </section>

          <section className="npp-section npp-section--children">
            <h4>{t(isFemale ? "profilePanel.childrenPregnancies" : "profilePanel.childrenOnly")}</h4>

            <div className="npp-childGrid">
              <div className="npp-field npp-field--compact">
                <label>{t("profilePanel.numberChildren")}</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.numberOfChildren}
                  placeholder={additionalInfoLoading ? t("profilePanel.loading") : "—"}
                  onChange={(event) => updateField("numberOfChildren", event.target.value)}
                />
              </div>

              {isFemale && (
                <>
                  <div className="npp-field npp-field--compact">
                    <label>{t("profilePanel.numberPregnancies")}</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.numberOfPregnancies}
                      placeholder={additionalInfoLoading ? t("profilePanel.loading") : "—"}
                      onChange={(event) => updateField("numberOfPregnancies", event.target.value)}
                    />
                  </div>

                  <div className="npp-field npp-field--compact">
                    <label>{t("profilePanel.complications")}</label>
                    <input
                      value={form.pregnancyComplications}
                      onChange={(event) => updateField("pregnancyComplications", event.target.value)}
                    />
                  </div>

                  <div className="npp-field npp-field--compact">
                    <label>{t("profilePanel.curettageOrStoppedPregnanciesCount")}</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.curettageOrStoppedPregnanciesCount}
                      placeholder={additionalInfoLoading ? t("profilePanel.loading") : "—"}
                      onChange={(event) =>
                        updateField("curettageOrStoppedPregnanciesCount", event.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </section>
        </div>

        <div className="npp-footer">
          <button type="button" className="npp-button npp-button--secondary" onClick={onClose}>
            {t("profilePanel.close")}
          </button>
          <button
            type="button"
            className="npp-button npp-button--primary"
            onClick={saveAdditionalInfo}
            disabled={isSaving || additionalInfoLoading || !patientId}
          >
            {isSaving ? t("profilePanel.saving") : t("profilePanel.save")}
          </button>
          <button type="button" className="npp-helpButton" aria-label={t("profilePanel.help")}>
            ?
          </button>
        </div>
      </aside>
    </>
  );
}
